package ui.company

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.focus.*
import androidx.compose.ui.unit.*
import common.frontend.api.*
import compose.assets.*
import compose.assets.svg.*
import kotlinx.coroutines.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.company.CompanyListingScope.Companion.headerSpecs
import ui.company.detail.*
import ui.widget.*

@ComposableNavNode
@Composable
fun CompanyListingUI() {
    with(CompanyListingScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}


@OptIn(ExperimentalFoundationApi::class)
@Composable
fun CompanyListingScope.Content(nnvl: CompanyListingScope.Wrapper) {
    val itemCreatorState = DialogState.remember()
    ItemCreatorDialog(itemCreatorState)

    val stateHorizontal = rememberScrollState(0)
    val itemMeasurer = headerSpecs.rememberMeasurer()
    val scrollState = nav.rememberLazyListState("companies")

    Column(Modifier.fillMaxSize().padding(top = 8.dp).padding(start = 8.dp)) {
        HomeSearchUI(vm.searchFiled, label = Res.string.company_listing__search)
        Space(8.dp)
        Box(Modifier.fillMaxSize()) {
            Box(
                Modifier.fillMaxSize()
                    .tableMeasurer(itemMeasurer)
                    .horizontalScroll(stateHorizontal)
            ) {
                LazyColumn(
                    state = scrollState,
                    contentPadding = PaddingValues(end = 8.dp),
                    modifier = Modifier.fillMaxHeight()
                        .width(itemMeasurer.rowWidth)
                ) {
                    stickyHeader {
                        ListingHeader(headerSpecs)
                    }
                    itemsIndexed(
                        SearchWrapper.use(
                            fields = arrayOf(
                                Io.ListingCompany.Item::id,
                                Io.ListingCompany.Item::idHex,
                                Io.ListingCompany.Item::name
                            ),
                            items = nnvl.state,
                            query = vm.searchFiled,
                            sort = compareByDescending {
                                if (it.data.id < 1000) Long.MAX_VALUE - it.data.id else it.data.id
                            }
                        )) { _, item ->
                        ListingDivider()
                        ListingItem(item, headerSpecs) {
                            nav.findRoot().navTo_CompanyUI(CompanyArgs(item.data.id))
                        }
                    }
                    ListingFooter()
                }
            }
            HorizontalScrollbar(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(bottom = 8.dp)
                    .fillMaxWidth(),
                adapter = rememberScrollbarAdapter(stateHorizontal)
            )
            if (nnvl.canAdd) {
                FloatingActionButton(
                    onClick = { itemCreatorState.show() },
                    modifier = Modifier.align(Alignment.BottomEnd)
                        .padding(24.dp)
                ) {
                    Icon(Svg.Plus, null)
                }
            }
        }
    }
}

@Composable
fun CompanyListingScope.ItemCreatorDialog(state: DialogState) {
    Dialog(state) {
        val item = remember {
            CompanyListingScope.Wrapper.Item(
                Io.ListingCompany.Item.EMPTY
            )
        }
        val focus = remember {
            FocusRequester()
        }
        var canSave by remember { mutableStateOf(true) }
        DialogUI(
            onDismissRequest = { state.hide() },
            modifier = Modifier.align(Alignment.Center),
            confirmButton = {
                TextButton(
                    onClick = {
                        canSave = false
                        action(CompanyListingVM.Action.New(item.data))
                        state.hide()
                    },
                    enabled = item.change && canSave
                ) { Text(Res.string.dialog__action_save.rememberString()) }
            },
            dismissButton = {
                TextButton(
                    onClick = { state.hide() }
                ) { Text(Res.string.dialog__action_cancel.rememberString()) }
            },

            title = { Text(Res.string.company_listing__new_title.rememberString()) }
        ) {
            TextField(
                value = item.name.text,
                modifier = Modifier.focusRequester(focus),
                leadingIcon = { Icon(Svg.Format_text, null) },
                placeholder = { Text(Res.string.company_listing__new_name_hint.rememberString()) },
                onValueChange = {
                    item.name.set(it)
                })
        }
        LaunchedEffect(Unit) {
            delay(100)
            focus.requestFocus()
        }
    }
}

